import React, { useEffect, useState } from 'react';
import { Form, Input, Row, Col, Button, Space, Select, Tooltip, Alert, Typography } from 'antd';
import { nameRules, descriptionRules } from 'common/FormValidationRules';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import CypressTestIds from "../../../cypress/CypressTestIds";

const { Option } = Select;

const fallbackKeyTypes = [
    { keyType: 'OPEN_AI_KEY', label: 'Open AI' },
    { keyType: 'GOOGLE_MAP_KEY', label: 'Google Maps' },
];

const ManageKeys = ({ action, keyData, updateKey, createKey, onCancel, formErrors, keyTypeCatalog, onTestKey, validateKeyResult }) => {
    const [form] = Form.useForm();
    const [readOnlyView] = useState(action === 'view');
    const [selectedType, setSelectedType] = useState(keyData ? keyData.type : undefined);
    const [testing, setTesting] = useState(false);
    const [hasTested, setHasTested] = useState(false);
    const [testResult, setTestResult] = useState(null);

    useEffect(() => {
        if (keyData) {
            form.setFieldsValue({ ...keyData });
            setSelectedType(keyData.type);
        }
    }, [keyData, form]);

    useEffect(() => {
        if (hasTested) {
            setTesting(false);
            setTestResult(validateKeyResult);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [validateKeyResult]);

    const catalog = (keyTypeCatalog && keyTypeCatalog.length) ? keyTypeCatalog : fallbackKeyTypes;
    const selectedEntry = catalog.find((k) => k.keyType === selectedType);

    const onTypeChange = (value) => {
        setSelectedType(value);
        setTestResult(null);
        setHasTested(false);
    };

    const gatherSettings = () => {
        const settings = {};
        (selectedEntry && selectedEntry.testFields ? selectedEntry.testFields : []).forEach((f) => {
            const v = form.getFieldValue(['settings', f.name]);
            if (v) settings[f.name] = v;
        });
        return settings;
    };

    const onTest = () => {
        if (!selectedType || !onTestKey) return;
        setHasTested(true);
        setTesting(true);
        setTestResult(null);
        onTestKey({ type: selectedType, key: form.getFieldValue('key') || '', settings: gatherSettings() });
    };

    const onFormSubmit = (formData) => {
        const { settings, ...rest } = formData;
        if (action === 'edit' && keyData) {
            rest.version = keyData.version;
        }
        const jsonData = JSON.stringify(rest);
        if (action === 'edit') {
            updateKey(jsonData, keyData.keyId);
        } else {
            createKey(jsonData);
        }
    };

    const keyRules = action === 'edit' ? [] : [{ required: true, message: 'Please input the API key!' }];
    const getFormErrors = (fieldName) => (formErrors && formErrors[fieldName] ? { help: formErrors[fieldName], validateStatus: 'error' } : {});

    const showTest = !readOnlyView && selectedEntry && selectedEntry.supportsTestKey && onTestKey;
    const showRoutingHint = !readOnlyView && selectedEntry && selectedEntry.provider && selectedEntry.provider !== 'GOOGLE';

    return (
        <>
            <Form labelCol={{ span: 3 }} layout="horizontal" labelAlign="left" form={form} name="manageKeys" onFinish={onFormSubmit} initialValues={keyData}>
                <Row gutter={[16, 8]}>
                    <Col span={24} style={{ textAlign: 'left' }}>
                        <Form.Item label="Name" name="name" rules={nameRules} {...getFormErrors('name')}>
                            <Input readOnly={readOnlyView} placeholder='Enter the name' data-testid={CypressTestIds.SYSTEM_KEYS_MANAGE_NAME_INPUT} />
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={[16, 8]}>
                    <Col span={24}>
                        <Form.Item label="Description" name="description" rules={descriptionRules} {...getFormErrors('description')}>
                            <Input readOnly={readOnlyView} placeholder='Enter the description' data-testid={CypressTestIds.SYSTEM_KEYS_MANAGE_DESCRIPTION_INPUT} />
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={[16, 8]}>
                    <Col span={24}>
                        <Form.Item
                            label="Type"
                            name="type"
                            rules={[{ required: true, message: 'Please select the key type!' }]}
                            {...getFormErrors('type')}
                            extra={selectedEntry && selectedEntry.helpUrl ? (
                                <a href={selectedEntry.helpUrl} target="_blank" rel="noopener noreferrer">
                                    How to get a {selectedEntry.label} key ↗
                                </a>
                            ) : null}
                        >
                            <Select
                                disabled={readOnlyView || action === 'edit'}
                                placeholder="Select the key type"
                                onChange={onTypeChange}
                                data-testid={CypressTestIds.SYSTEM_KEYS_MANAGE_TYPE_SELECT}
                            >
                                {catalog.map((k) => (
                                    <Option key={k.keyType} value={k.keyType}>{k.label}</Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>
                </Row>

                {!readOnlyView && selectedEntry && (selectedEntry.testFields || []).map((field) => (
                    <Row gutter={[16, 8]} key={field.name}>
                        <Col span={24}>
                            <Form.Item
                                label={field.label}
                                name={['settings', field.name]}
                                extra={field.help || 'Used to test the key. Save connection settings under AI Model Routing.'}
                            >
                                <Input placeholder={field.placeholder} />
                            </Form.Item>
                        </Col>
                    </Row>
                ))}

                <Row gutter={[16, 8]}>
                    <Col span={24}>
                        <Form.Item
                            label={
                                <span>
                                    API Key&nbsp;
                                    {action === "edit" && (
                                        <Tooltip title="For security and audit compliance, API keys cannot be modified. To update the key value, please delete this key and create a new one.">
                                            <FontAwesomeIcon icon="war" style={{ color: "#ff7a00" }} cursor={'pointer'} />
                                        </Tooltip>
                                    )}
                                </span>
                            }
                            name="key"
                            rules={keyRules}
                            {...getFormErrors('key')}
                        >
                            <Input.Password disabled={action === 'edit'} readOnly={readOnlyView} placeholder={action === 'edit' ? 'API key cannot be modified (delete and recreate to change)' : 'Enter the API key here'} data-testid={CypressTestIds.SYSTEM_KEYS_MANAGE_API_KEY_INPUT} />
                        </Form.Item>
                    </Col>
                </Row>

                {showTest && (
                    <Row gutter={[16, 8]}>
                        <Col span={24} style={{ textAlign: 'left', paddingLeft: '12.5%' }}>
                            <Space direction="vertical" style={{ width: '100%' }}>
                                <Button onClick={onTest} loading={testing}>Test key</Button>
                                {testResult && (
                                    <Alert
                                        type={testResult.ok ? 'success' : (testResult.outcome === 'needs-config' ? 'warning' : 'error')}
                                        showIcon
                                        message={testResult.ok ? 'Key is valid.' : (testResult.message || 'Key test failed.')}
                                    />
                                )}
                            </Space>
                        </Col>
                    </Row>
                )}

                {showRoutingHint && (
                    <Row gutter={[16, 8]}>
                        <Col span={24} style={{ paddingLeft: '12.5%' }}>
                            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                                Choose which capabilities use this provider under <Typography.Text strong>AI Model Routing</Typography.Text>.
                            </Typography.Text>
                        </Col>
                    </Row>
                )}
                <Row gutter={[16, 16]}>
                    <Col span={24} className="footer-right-column">
                        <Space>
                            <Button onClick={onCancel} data-testid={CypressTestIds.SYSTEM_KEYS_MANAGE_CANCEL_BUTTON}>Cancel</Button>
                            <Button type="primary" htmlType="submit" disabled={readOnlyView} data-testid={CypressTestIds.SYSTEM_KEYS_MANAGE_SAVE_BUTTON}>
                                Save
                            </Button>
                        </Space>
                    </Col>
                </Row>
            </Form>
        </>
    );
};

export default ManageKeys;
